import React, { useEffect, useMemo, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt, faCalendarDays, faSackDollar, faBoxesStacked, faArrowsRotate, faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
// общий стиль отчётных страниц Amazon (air-*) + локальные дополнения (adl-*)
import './AmazonInventoryReportPage.css';
import './AmazonDealsPage.css';

// fixed categorical order, CVD-validated: green/blue/amber/magenta;
// identity is never color-alone — every chart ships legend + counts
const TYPES = {
  DEAL: { label: 'Deal', color: '#5B965D' },
  COUPON: { label: 'Coupon', color: '#4a7fc4' },
  PRICE_DISCOUNT: { label: 'Price Discount', color: '#d9931a' },
  BASKET_BUILDING: { label: 'Basket Building', color: '#b0568f' },
};
const TYPE_ORDER = ['DEAL', 'COUPON', 'PRICE_DISCOUNT', 'BASKET_BUILDING'];
const STATUS_PILL = { UPCOMING: 'warn', RUNNING: 'ok', EXPIRED: 'muted', CANCELLED: 'muted', FAILED: 'bad' };
const STATUS_ORDER = ['UPCOMING', 'RUNNING', 'EXPIRED', 'CANCELLED', 'FAILED'];

const fmt = (n) => (n === null || n === undefined) ? '—' : Number(n).toLocaleString('en-US');
const num = (n) => Number(n) || 0;
const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const money2 = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// DATE columns arrive as 'YYYY-MM-DD' — never new Date(), timezone would shift the day
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateParts = (s) => String(s || '').slice(0, 10).split('-').map(Number);
const dayLabel = (s) => { const [, m, d] = dateParts(s); return m ? `${MONTHS_SHORT[m - 1]} ${d}` : '—'; };
const rangeLabel = (a, b) => {
  const [y1] = dateParts(a); const [y2] = dateParts(b);
  if (!y1) return '—';
  return `${dayLabel(a)} – ${dayLabel(b)}${y2 ? `, ${y2}` : ''}${y1 !== y2 ? ` (${y1})` : ''}`;
};
const daysFromToday = (s) => {
  const [y, m, d] = dateParts(s);
  if (!y) return null;
  const now = new Date();
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
};
const monthKey = (s) => String(s || '').slice(0, 7);

const discountLabel = (type, value) => {
  if (value === null || value === undefined) return null;
  if (type === 'PERCENTAGE_OFF') return `${Number(value)}% off`;
  if (type === 'AMOUNT_OFF') return `${money2(value)} off`;
  if (type === 'FREE_ITEM') return 'free item';
  return null;
};
const feesLabel = (p) => {
  if (p.upfront_fee === null && p.variable_fee_pct === null) return null;
  const parts = [];
  if (p.upfront_fee !== null) parts.push(money(p.upfront_fee));
  if (p.variable_fee_pct !== null) parts.push(`${(Number(p.variable_fee_pct) * 100).toFixed(1).replace(/\.0$/, '')}% of sales`);
  return parts.join(' + ');
};

const TOOLTIP_STYLE = {
  background: '#fff', border: 'none', borderRadius: 12,
  boxShadow: '0 8px 24px rgba(20,40,25,0.14)', fontSize: 12, padding: '10px 14px',
};
const AXIS_TICK = { fontSize: 11, fill: '#9ba3b0' };

const Pill = ({ status }) => <span className={`air-pill ${STATUS_PILL[status] || 'muted'}`}>{status}</span>;
const SUBTYPES = { LIGHTNING_DEAL: 'Lightning Deal', BEST_DEAL: 'Best Deal' };
const TypeDot = ({ type, subtype }) => (
  <span className="adl-type">
    <span className="air-dot" style={{ background: (TYPES[type] || {}).color || '#9ba3b0' }} />
    {SUBTYPES[subtype] || (TYPES[type] || {}).label || type}
  </span>
);

// ---------------------------------------------------------------------------
// Donut: promotions run in the last 12 months, by type
// ---------------------------------------------------------------------------
const MixDonut = ({ parts, total }) => (
  <div className="air-donut">
    <div className="air-donut-chart">
      <ResponsiveContainer width={150} height={150}>
        <PieChart>
          <Pie
            data={parts} dataKey="value" nameKey="label"
            innerRadius="68%" outerRadius="100%"
            startAngle={90} endAngle={-270}
            paddingAngle={2} stroke="#fff" strokeWidth={2} isAnimationActive={false}
          >
            {parts.map(p => <Cell key={p.label} fill={p.color} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [fmt(v), name]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="air-donut-center">
        <div className="air-donut-total">{fmt(total)}</div>
        <div className="air-donut-caption">promos · 12 mo</div>
      </div>
    </div>
    <div className="air-donut-legend">
      {parts.map(p => (
        <div key={p.label} className="air-donut-row">
          <span className="air-dot" style={{ background: p.color }} />
          <span className="air-donut-name">{p.label}</span>
          <b>{fmt(p.value)}</b>
          <i>{total ? Math.round((p.value / total) * 100) : 0}%</i>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Monthly revenue from promotions (performance report data)
// ---------------------------------------------------------------------------
const RevenueChart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 0 }} barCategoryGap="30%">
      <CartesianGrid vertical={false} stroke="#eef1ee" />
      <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
      <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={money} width={64} />
      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#f2f6f2' }}
               formatter={(v, name, p) => [`${money(v)} · ${fmt(p.payload.units)} units · ${p.payload.deals} promos`, 'Revenue']} />
      <Bar dataKey="revenue" name="Revenue" fill="#5B965D" radius={[7, 7, 7, 7]} maxBarSize={26} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const AmazonDealsPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // cancelled/failed промо скрыты по умолчанию — их 100+ и они шум
  const [statuses, setStatuses] = useState(() => new Set(['UPCOMING', 'RUNNING', 'EXPIRED']));
  const [types, setTypes] = useState(() => new Set(TYPE_ORDER));

  const loadReport = () => api.get('/api/amazon-promotions')
    .then(({ data }) => { setData(data); return data; })
    .catch((e) => { setError(e.response?.data?.error || e.message); return null; });

  useEffect(() => { loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncError(null);
    const prevSync = data?.lastSync || null;
    try {
      // сервер отвечает сразу и синкает в фоне; первый прогон тянет
      // товары всех промо и отчёт результативности — до 15-20 минут
      await api.post('/api/amazon-promotions/sync');
      const deadline = Date.now() + 25 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 20000));
        const fresh = await loadReport();
        if (fresh && fresh.lastSync !== prevSync && !fresh.syncing) { setSyncing(false); return; }
      }
      setSyncError('Sync is taking longer than usual — refresh the page later');
    } catch (e) {
      setSyncError(e.response?.data?.error || e.message);
    }
    setSyncing(false);
  };

  const toggleIn = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleStatus = toggleIn(setStatuses);
  const toggleType = toggleIn(setTypes);
  const toggleExpand = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const promos = useMemo(() => data?.promotions || [], [data]);
  const names = data?.names || {};
  const nameOf = (asin) => names[asin] || asin;

  const statusCounts = useMemo(() => {
    const c = {};
    promos.forEach(p => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [promos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return promos.filter(p => {
      if (!statuses.has(p.status) || !types.has(p.promotion_type)) return false;
      if (!q) return true;
      if ((p.title || '').toLowerCase().includes(q)) return true;
      if ((p.event_id || '').toLowerCase().includes(q)) return true;
      return p.items.some(it => it.asin.toLowerCase().includes(q)
        || (it.sku || '').toLowerCase().includes(q)
        || nameOf(it.asin).toLowerCase().includes(q));
    });
  }, [promos, search, statuses, types]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Статистика ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const upcoming = promos.filter(p => p.status === 'UPCOMING')
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
    const running = promos.filter(p => p.status === 'RUNNING');

    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
    const last12 = promos.filter(p => p.status !== 'CANCELLED' && p.status !== 'FAILED'
      && p.start_date && p.start_date >= yearAgo);
    const withPerf = promos.filter(p => p.perf_revenue !== null && p.start_date && p.start_date >= yearAgo);
    const revenue12 = withPerf.reduce((s, p) => s + num(p.perf_revenue), 0);
    const units12 = withPerf.reduce((s, p) => s + num(p.perf_units), 0);

    const mix = TYPE_ORDER
      .map(t => ({ label: TYPES[t].label, color: TYPES[t].color, value: last12.filter(p => p.promotion_type === t).length }))
      .filter(p => p.value > 0);

    // выручка по месяцам старта промо, последние 24 месяца с данными
    const byMonth = new Map();
    promos.forEach(p => {
      if (p.perf_revenue === null || !p.start_date) return;
      const k = monthKey(p.start_date);
      const m = byMonth.get(k) || { month: k.slice(2), revenue: 0, units: 0, deals: 0 };
      m.revenue += num(p.perf_revenue); m.units += num(p.perf_units); m.deals += 1;
      byMonth.set(k, m);
    });
    const monthly = [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-24).map(([, v]) => v);

    const topDeals = promos
      .filter(p => num(p.perf_revenue) > 0)
      .sort((a, b) => num(b.perf_revenue) - num(a.perf_revenue))
      .slice(0, 10);

    return { upcoming, running, revenue12, units12, hasPerf: withPerf.length > 0, mix, mixTotal: last12.length, monthly, topDeals };
  }, [promos]);

  if (error) return (<><HeaderLogged /><div className="air-canvas"><div className="air-page"><div className="air-error">{error}</div></div></div></>);
  if (!data) return (<><HeaderLogged /><div className="air-canvas"><div className="air-page"><div className="air-loading">Loading deals…</div></div></div></>);

  const next = stats.upcoming[0];
  const nextDays = next ? daysFromToday(next.start_date) : null;

  const tiles = [
    { label: 'Upcoming', value: stats.upcoming.length, icon: faCalendarDays,
      sub: next ? `next: ${rangeLabel(next.start_date, next.end_date)}${nextDays !== null ? ` · in ${nextDays} d` : ''}` : 'nothing scheduled' },
    { label: 'Running now', value: stats.running.length, icon: faBolt,
      sub: stats.running.length ? [...new Set(stats.running.map(p => (TYPES[p.promotion_type] || {}).label || p.promotion_type))].join(' · ') : null },
    { label: 'Deal revenue · 12 mo', value: stats.hasPerf ? money(stats.revenue12) : '—', icon: faSackDollar,
      sub: 'from the Amazon promotion performance report' },
    { label: 'Units sold in deals · 12 mo', value: stats.hasPerf ? fmt(stats.units12) : '—', icon: faBoxesStacked,
      sub: null },
  ];

  const liveCards = [...stats.running, ...stats.upcoming];

  return (
    <>
      <HeaderLogged />
      <div className="air-canvas">
        <div className="air-page">
          <div className="air-header">
            <div>
              <h2>Amazon Deals</h2>
              <p className="air-subtitle">deals · coupons · price discounts · basket building — schedule, products &amp; performance from Amazon</p>
            </div>
            <div className="air-header-right">
              <span className="air-sync">Last sync: {data.lastSync ? new Date(data.lastSync).toLocaleString() : '—'}</span>
              <button className="air-sync-btn" onClick={handleSync} disabled={syncing || data.syncing}>
                <FontAwesomeIcon icon={faArrowsRotate} spin={syncing || data.syncing} />
                {(syncing || data.syncing) ? 'Syncing… (takes 10-15 min)' : 'Sync now'}
              </button>
            </div>
          </div>
          {syncError && <div className="air-error" style={{ marginBottom: 16 }}>Sync failed: {syncError}</div>}

          {promos.length === 0 ? (
            <div className="air-hint">
              <b>No promotions yet.</b> Press <b>Sync now</b> to pull deals, coupons and discounts from Amazon.
              Products of upcoming and running promos arrive with the first sync (~10-15 min); the Amazon API is
              heavily rate-limited, so product lists of past promotions fill in gradually with the daily syncs.
            </div>
          ) : (
            <>
              {/* ── Hero: tiles + type mix donut ─────────────────── */}
              <div className="air-hero">
                <div className="air-tiles">
                  {tiles.map(t => (
                    <div key={t.label} className="air-tile">
                      <span className="air-tile-icon"><FontAwesomeIcon icon={t.icon} /></span>
                      <div>
                        <div className="air-tile-value">{t.value}</div>
                        <div className="air-tile-label">{t.label}</div>
                        {t.sub && <div className="air-tile-sub">{t.sub}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="air-card air-hero-donut">
                  <h3>Promo mix <span className="air-chip">12 mo</span></h3>
                  {stats.mixTotal > 0
                    ? <MixDonut parts={stats.mix} total={stats.mixTotal} />
                    : <div className="air-empty">No promotions in the last 12 months.</div>}
                </div>
              </div>

              {/* ── Upcoming & running cards ─────────────────────── */}
              {liveCards.length > 0 && (
                <>
                  <div className="air-group-label">Upcoming &amp; running — products in each promo</div>
                  <div className="adl-cards">
                    {liveCards.map(p => (
                      <div key={p.promotion_id} className={`air-card adl-deal-card${p.status === 'RUNNING' ? ' running' : ''}`}>
                        <div className="adl-deal-top">
                          <TypeDot type={p.promotion_type} subtype={p.deal_subtype} />
                          <Pill status={p.status} />
                        </div>
                        <div className="adl-deal-dates">
                          {rangeLabel(p.start_date, p.end_date)}
                          {p.event_id && <span className="air-chip warn">{p.event_id.split('/')[0]}</span>}
                        </div>
                        <div className="adl-deal-title" title={p.title || ''}>{p.title || '—'}</div>
                        <div className="adl-deal-meta">
                          {discountLabel(p.discount_type, p.discount_value) && <span className="air-fee-chip"><b>{discountLabel(p.discount_type, p.discount_value)}</b></span>}
                          {p.budget_value !== null && <span className="air-fee-chip">budget <b>{p.budget_type === 'AMOUNT' ? money(p.budget_value) : `${fmt(p.budget_value)} units`}</b></span>}
                          {feesLabel(p) && <span className="air-fee-chip">fees <b>{feesLabel(p)}</b></span>}
                        </div>
                        {p.items.length > 0 && (
                          <div className="adl-items">
                            {p.items.map(it => (
                              <div key={it.asin} className="adl-item">
                                <div className="adl-item-name">
                                  {nameOf(it.asin)}
                                  <span className="air-asin"> {it.asin}</span>
                                </div>
                                <div className="adl-item-price">
                                  {it.deal_price !== null ? money2(it.deal_price)
                                    : discountLabel(it.discount_type, it.discount_value) || '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Monthly revenue ──────────────────────────────── */}
              <div className="air-card">
                <h3>Revenue from promotions by month <span className="air-chip">performance report</span></h3>
                {stats.monthly.length > 0
                  ? <RevenueChart rows={stats.monthly} />
                  : <div className="air-empty">No performance data yet — it arrives with the sync (Amazon builds the report for deals that already ran).</div>}
                <p className="air-note">
                  Sales attributed by Amazon to each promotion, grouped by the month the promotion <b>started</b>.
                  Hover a bar for units and the number of promotions. Source: GET_PROMOTION_PERFORMANCE_REPORT (deals only —
                  Amazon doesn't report coupon/basket promos here), up to 2 years back.
                </p>
              </div>

              {/* ── Top deals ────────────────────────────────────── */}
              {stats.topDeals.length > 0 && (
                <div className="air-card">
                  <h3>Top deals by revenue <span className="air-chip">2 years</span></h3>
                  <div className="air-table-wrap">
                    <table className="air-table">
                      <thead><tr>
                        <th>Dates</th><th>Promotion</th><th>Type</th>
                        <th className="air-num">Revenue</th><th className="air-num">Units</th><th className="air-num">Glance views</th>
                      </tr></thead>
                      <tbody>
                        {stats.topDeals.map(p => (
                          <tr key={p.promotion_id}>
                            <td className="air-mono">{rangeLabel(p.start_date, p.end_date)}</td>
                            <td><div className="air-product adl-clamp">{p.title || '—'}</div></td>
                            <td><TypeDot type={p.promotion_type} subtype={p.deal_subtype} /></td>
                            <td className="air-num air-strong">{money(p.perf_revenue)}</td>
                            <td className="air-num">{fmt(p.perf_units)}</td>
                            <td className="air-num">{fmt(p.perf_glance_views)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── All promotions ───────────────────────────────── */}
              <div className="air-card">
                <div className="air-section-head">
                  <h3>All promotions <span className="air-chip">{filtered.length} of {promos.length}</span></h3>
                  <input
                    className="air-search" placeholder="Search title, event, ASIN, SKU…"
                    value={search} onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="adl-filters">
                  <div className="air-brand-filter">
                    {STATUS_ORDER.filter(s => statusCounts[s]).map(s => (
                      <button key={s} className={`air-brand-btn${statuses.has(s) ? '' : ' off'}`} onClick={() => toggleStatus(s)}>
                        {s.toLowerCase()} {statusCounts[s]}
                      </button>
                    ))}
                  </div>
                  <div className="air-brand-filter">
                    {TYPE_ORDER.map(t => (
                      <button key={t} className={`air-brand-btn${types.has(t) ? '' : ' off'}`} onClick={() => toggleType(t)}>
                        <span className="air-dot" style={{ background: TYPES[t].color, marginRight: 6 }} />{TYPES[t].label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="air-table-wrap air-table-tall">
                  <table className="air-table">
                    <thead><tr>
                      <th>Dates</th><th>Promotion</th><th>Type</th><th>Status</th>
                      <th className="air-num">Products</th><th>Discount</th><th>Fees</th>
                      <th className="air-num">Revenue</th><th className="air-num">Units</th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(p => (
                        <React.Fragment key={p.promotion_id}>
                          <tr className={p.items.length ? 'adl-row' : ''} onClick={() => p.items.length && toggleExpand(p.promotion_id)}>
                            <td className="air-mono">{rangeLabel(p.start_date, p.end_date)}</td>
                            <td>
                              <div className="air-product adl-clamp">
                                {p.items.length > 0 && (
                                  <FontAwesomeIcon icon={faChevronDown} className={`adl-caret${expanded.has(p.promotion_id) ? ' open' : ''}`} />
                                )}
                                {p.title || '—'}
                              </div>
                              {p.event_id && <div className="air-asin">{p.event_id}</div>}
                            </td>
                            <td><TypeDot type={p.promotion_type} subtype={p.deal_subtype} /></td>
                            <td><Pill status={p.status} /></td>
                            <td className="air-num">{p.items.length || <span className="air-zero">—</span>}</td>
                            <td>{discountLabel(p.discount_type, p.discount_value)
                              || (p.items.some(it => it.deal_price !== null) ? 'deal price' : <span className="air-zero">—</span>)}</td>
                            <td>{feesLabel(p) || <span className="air-zero">—</span>}</td>
                            <td className="air-num air-strong">{p.perf_revenue !== null ? money(p.perf_revenue) : <span className="air-zero">—</span>}</td>
                            <td className="air-num">{p.perf_units !== null ? fmt(p.perf_units) : <span className="air-zero">—</span>}</td>
                          </tr>
                          {expanded.has(p.promotion_id) && (
                            <tr className="adl-expand">
                              <td colSpan={9}>
                                <div className="adl-expand-items">
                                  {p.items.map(it => (
                                    <div key={it.asin} className="adl-item">
                                      <div className="adl-item-name">
                                        {nameOf(it.asin)}
                                        <span className="air-asin"> {it.asin}{it.sku ? ` · ${it.sku}` : ''}</span>
                                      </div>
                                      <div className="adl-item-price">
                                        {it.deal_price !== null ? money2(it.deal_price)
                                          : discountLabel(it.discount_type, it.discount_value) || '—'}
                                        {it.budget_units !== null && <span className="adl-item-budget"> · up to {fmt(it.budget_units)} u</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="air-note">
                  All promotions from the Amazon Promotions API — <b>upcoming, running and past</b>. Click a row to see
                  participating products with their deal prices. <b>Revenue / Units</b> — Amazon's attribution from the
                  promotion performance report (deals only, appears after a deal has run). <b>Fees</b> — the deal fee
                  Amazon quoted when the promotion was created. Cancelled and failed promos are hidden by default —
                  use the status filters to show them.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AmazonDealsPage;
